// Time on the dashboard is always India time, and every number says how old it is.
import { useEffect, useState } from "react";

const IST = "Asia/Kolkata";

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "10:42:13 IST" */
export function formatIST(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return iso ? String(iso) : "";
  const t = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).format(d);
  return `${t} IST`;
}

/** "Wed 2 Sep, 10:42 IST" */
export function formatISTDateTime(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return iso ? String(iso) : "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}, ${get("hour")}:${get("minute")} IST`;
}

/** "Wed 2 Sep" */
export function formatISTDate(iso: string | null | undefined): string {
  const d = parse(iso);
  if (!d) return iso ? String(iso) : "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, weekday: "short", day: "numeric", month: "short",
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}`;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

/** "14 seconds ago", "3 minutes ago", "2 hours ago", "4 days ago" */
export function ago(iso: string | null | undefined, now: number = Date.now()): string {
  const d = parse(iso);
  if (!d) return "";
  const s = Math.max(0, Math.round((now - d.getTime()) / 1000));
  if (s < 60) return `${plural(s, "second")} ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${plural(m, "minute")} ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${plural(h, "hour")} ago`;
  return `${plural(Math.round(h / 24), "day")} ago`;
}

/** "1m 12s" between two instants (the end defaults to now). */
export function elapsed(startIso: string | null | undefined, endIso?: string | null, now: number = Date.now()): string {
  const start = parse(startIso);
  if (!start) return "";
  const end = parse(endIso) ?? new Date(now);
  const s = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m ${r}s`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

/** The current time, re-read every `ms` while `active`. */
export function useTick(active: boolean, ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [active, ms]);
  return now;
}

/** "14 seconds ago", ticking every 5 seconds. */
export function useReadAgo(iso: string | null | undefined): string {
  const now = useTick(!!iso, 5000);
  return ago(iso, now);
}
